'use memo'

import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { TeamFinderRow } from '@sportninja/common/features/teamFinder/resultRow'
import TeamNameCell from '../components/cells/TeamNameCell'
import LinkTextCell from '../components/cells/LinkTextCell'
import ChipListCell from '../components/cells/ChipListCell'
import RegisterCell from '../components/cells/RegisterCell'

/**
 * The table's columns — one per value the card shows.
 *
 * Sort keys are the plain display strings, so a column sorts by what the reader
 * can actually see. `spotsRemaining` is the exception: it stays numeric so 10
 * sorts above 9 rather than lexically beneath it.
 */
export const useResultColumns = (): ColDef<TeamFinderRow>[] => {
  'use memo'

  return [
    {
      field: 'teamName',
      headerName: capitalizeWords('Team'),
      cellRenderer: TeamNameCell,
      minWidth: 220,
      flex: 2,
      pinned: 'left',
    },
    {
      field: 'competitionName',
      headerName: capitalizeWords('Competition'),
      minWidth: 200,
      flex: 2,
      cellRenderer: ({ data }: ICellRendererParams<TeamFinderRow>) =>
        data ? (
          <LinkTextCell
            label={data.competitionName}
            href={data.scheduleId ? `/schedule/${data.scheduleId}` : undefined}
          />
        ) : null,
    },
    {
      field: 'organizationName',
      headerName: capitalizeWords('Organization'),
      minWidth: 180,
      flex: 2,
      cellRenderer: ({ data }: ICellRendererParams<TeamFinderRow>) =>
        data ? (
          <LinkTextCell
            label={data.organizationName}
            href={
              data.organizationId ? `/org/${data.organizationId}` : undefined
            }
          />
        ) : null,
    },
    {
      field: 'divisionName',
      headerName: capitalizeWords('Division'),
      minWidth: 160,
      flex: 2,
    },
    {
      field: 'sportName',
      headerName: capitalizeWords('Sport'),
      minWidth: 120,
      flex: 1,
    },
    {
      field: 'genderLabel',
      headerName: capitalizeWords('Gender'),
      minWidth: 120,
      flex: 1,
    },
    {
      field: 'ageGroup',
      headerName: capitalizeWords('Age Group'),
      minWidth: 110,
      flex: 1,
    },
    {
      field: 'scheduleDates',
      headerName: capitalizeWords('Competition Dates'),
      minWidth: 190,
      flex: 2,
    },
    {
      field: 'availableDays',
      headerName: capitalizeWords('Available Days'),
      minWidth: 200,
      flex: 2,
      // An array has no natural sort or filter value, so give the column the
      // joined string to work with — otherwise AG Grid compares "[object
      // Array]" against itself and the column silently never sorts.
      valueGetter: ({ data }) => data?.availableDays.join(', ') ?? '',
      cellRenderer: ({ data }: ICellRendererParams<TeamFinderRow>) =>
        data ? <ChipListCell values={data.availableDays} /> : null,
    },
    {
      field: 'availableTimes',
      headerName: capitalizeWords('Available Times'),
      minWidth: 180,
      flex: 2,
      valueGetter: ({ data }) => data?.availableTimes.join(', ') ?? '',
      cellRenderer: ({ data }: ICellRendererParams<TeamFinderRow>) =>
        data ? <ChipListCell values={data.availableTimes} /> : null,
    },
    {
      field: 'positionNames',
      headerName: capitalizeWords('Positions Needed'),
      minWidth: 180,
      flex: 2,
      valueGetter: ({ data }) => data?.positionNames.join(', ') ?? '',
    },
    {
      field: 'spotsRemaining',
      headerName: capitalizeWords('Spots Left'),
      minWidth: 110,
      flex: 1,
      // Numeric, so 10 sorts above 9. Blank when the API omits the count —
      // never 0, which would assert the team is full.
      valueFormatter: ({ value }) => (value == null ? '' : String(value)),
    },
    {
      field: 'notes',
      headerName: capitalizeWords('Notes'),
      minWidth: 220,
      flex: 3,
      tooltipField: 'notes',
    },
    {
      colId: 'register',
      headerName: '',
      cellRenderer: RegisterCell,
      minWidth: 160,
      width: 160,
      pinned: 'right',
      // An action, not a value: nothing to sort or resize by.
      sortable: false,
      resizable: false,
    },
  ]
}
